/* global URL_MENU */
import { useState, cloneElement } from 'react';
import type { ReactNode, ReactElement } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import DefaultImgBussiness from '@assets/images/DefaultImgBussiness.png';
import BtnMenu from '@assets/images/iconos-aside-panel-menu.svg';
import BtnORders from '@assets/images/iconos-aside-panel-orders.svg';
import BtnPubli from '@assets/images/iconos-aside-panel-anuncio.svg';
import onlinePaymentIcon from '@assets/images/onlinePaymentIcon.svg';
import BtnQR from '@assets/images/iconos-aside-panel-qr.svg';
import CouponIcon from '@assets/images/iconos-aside-panel-coupons.svg';
import UsersIcon from '@assets/images/icono-mis clientes.svg';
import IconoQ from '@assets/images/icono-question.svg';
import priceIcon from '@assets/images/priceIcon.svg';
import IconoEstadisticas from '@assets/images/iconos-aside-panel-estadisticas.svg';
import { useAuth } from '@context/AuthContext';
import { useGetCommerce } from '@pages/Commerce/hooks/commerce';
import useModules from '@utils/hooks/useModules';
import NavBarBackground from './components/layout/NavBarBackground';
import NavBarContainerV2 from './components/layout/NavBarContainerV2';
import NavBarToggleButton from './components/interface/NavBarToggleButton';
import NavBarStatus from './components/interface/NavBarStatus';
import { ModuleName } from '@utils/constants';
import { useSubscription } from '@pages/Subscriptionsv2/hooks/useSubscriptions';
import Loading from '@components/LoadingProcess';
import { IconBuildingBridge2, IconSettingsAutomation, IconLinkPlus } from '@tabler/icons-react';
import { useShouldShowSetup } from '@pages/Setup/hooks/useShouldShowSetup';
import { Icon } from '@iconify/react';
import Tooltip from '@components/interface/Tooltip';
import { IconPointFilled } from '@tabler/icons-react';
import FounderContactModal from '@components/interface/FounderContactModal';
import FounderMessageForm from '@components/interface/FounderMessageForm';
import useTracking, { TRACK_EVENT_NAMES } from '@utils/hooks/useTracking';

const getNavigatePath = (hasModule: boolean, path: string) => {
  return hasModule ? path : 'suscripcion/' + path;
};

type NavButtonV3Props = {
  id: string;
  to: string;
  icon: string;
  isExpanded: boolean;
  label: string;
  notIncludedOnCurrentPlan?: boolean;
  tablerIcon?: ReactNode;
};

const NavButtonV3 = ({
  to,
  id,
  icon,
  isExpanded,
  label,
  notIncludedOnCurrentPlan = false,
  tablerIcon,
}: NavButtonV3Props) => {
  return (
    <Tooltip label={label} enabled={!isExpanded}>
      <NavLink
        to={notIncludedOnCurrentPlan ? `suscripcion/${to}` : to}
        className={({ isActive }) =>
          `relative flex items-center px-2 py-2 text-base rounded-md hover:bg-color-gray_light transition-colors ${
            isActive ? 'text-white bg-color-secondary border-l-[3px] border-[#2d6f86]' : 'text-color-gray_ultra_dark'
          }`
        }
        style={({ isActive }) => (isActive ? { color: '#ffffff' } : undefined)}
        id={id}
      >
        {({ isActive }) => (
          <>
            {tablerIcon ? (
              <div className="w-5 flex-shrink-0">
                {cloneElement(tablerIcon as ReactElement<{ color?: string }>, isActive ? { color: '#ffffff' } : {})}
              </div>
            ) : (
              <img
                src={icon}
                alt=""
                role="presentation"
                className={`w-5 flex-shrink-0${isActive ? ' brightness-0 invert' : ''}`}
              />
            )}
            <div
              className={`overflow-hidden transition-all ${isExpanded ? 'ml-2 w-full opacity-100' : 'w-0 opacity-0'}`}
            >
              <span className="whitespace-nowrap">{label}</span>
            </div>
            {notIncludedOnCurrentPlan && (
              <div className="ml-auto">
                <IconPointFilled className={isActive ? 'text-white' : 'text-color-secondary'} width={14} />
              </div>
            )}
          </>
        )}
      </NavLink>
    </Tooltip>
  );
};

// eslint-disable-next-line complexity
const NavBarDesktopV3 = () => {
  const navigate = useNavigate();
  const [isExpanded, setExpanded] = useState(() => window.innerWidth > 1280);
  const [isConfigOpen, setIsConfigOpen] = useState(true);
  const { user, logout } = useAuth();
  const { isFreemium, planId } = useSubscription();
  const { commerce, isFetching } = useGetCommerce();
  const { hasModule } = useModules();
  const shouldShowSetup = useShouldShowSetup();
  const [showFounderWarning, setShowFounderWarning] = useState(false);
  const [showFounderForm, setShowFounderForm] = useState(false);
  const { trackEvent } = useTracking();

  const handleGoToMenu = () => {
    if (!commerce.id_path) return;
    window.open(`${URL_MENU}/${commerce.id_path}`, '_blank');
  };

  return (
    <NavBarBackground isExpanded={isExpanded}>
      <NavBarToggleButton isExpanded={isExpanded} onToggle={() => setExpanded(!isExpanded)} />
      {!planId ? (
        <Loading />
      ) : (
        <NavBarContainerV2>
          {/* Sección superior: Logo e información */}
          <div className="pb-6 border-b" style={{ borderColor: '#e5e7eb' }}>
            {isExpanded ? (
              <div className="flex items-center gap-4">
                <div className="relative flex-shrink-0">
                  <div className="rounded-full border-4 overflow-hidden w-20 h-20" style={{ borderColor: '#374151' }}>
                    <img
                      src={commerce.logotipo || DefaultImgBussiness}
                      alt="Logo del comercio"
                      className="w-full h-full object-cover bg-color-white"
                    />
                  </div>
                  <div className="absolute top-0 right-0 bg-color-white rounded-full p-0.5 shadow-md">
                    <NavBarStatus isOpen={commerce.servicio_activo ?? true} showLabel={false} size="md" />
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <h2 className="text-base font-bold text-color-primary mb-1 truncate">{commerce.nombre}</h2>
                  <p className="text-sm text-color-gray_ultra_dark">
                    Bienvenido: <span className="font-bold text-color-primary">{user.username}</span>
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex justify-center">
                <div className="relative">
                  <div className="w-14 h-14 rounded-full border-4 overflow-hidden" style={{ borderColor: '#374151' }}>
                    <img
                      src={commerce.logotipo || DefaultImgBussiness}
                      alt="Logo del comercio"
                      className="w-full h-full object-cover bg-color-white"
                    />
                  </div>
                  <div className="absolute top-0 right-0 bg-color-white rounded-full p-0.5 shadow-sm">
                    <NavBarStatus isOpen={commerce.servicio_activo ?? true} showLabel={false} size="sm" />
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Sección media: botones con scroll */}
          <div className="flex-1 flex lg:flex-col mt-2 overflow-y-auto overflow-x-hidden">
            {shouldShowSetup && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="configuracion-inicial"
                id="configuracion-inicial-link"
                icon=""
                tablerIcon={<IconSettingsAutomation color="#4298b5" stroke={2} />}
                label="Configuración inicial"
              />
            )}

            {(isFreemium || hasModule(ModuleName.HISTORIAL)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="control-pedidos"
                id="control-pedidos-link"
                icon={BtnORders}
                label="Control de pedidos"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.HISTORIAL)}
              />
            )}

            {(isFreemium || hasModule(ModuleName.ESTADISTICAS)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="estadisticas"
                id="estadisticas-link"
                icon={IconoEstadisticas}
                label="Estadísticas"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.ESTADISTICAS)}
              />
            )}

            {(isFreemium || hasModule(ModuleName.MENU)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="menu"
                id="menu-link"
                icon={BtnMenu}
                label="Menú"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.MENU)}
              />
            )}

            {(isFreemium || hasModule(ModuleName.CUPONES)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="cupones"
                id="cupones-link"
                icon={CouponIcon}
                label="Cupones"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.CUPONES)}
              />
            )}

            {(isFreemium || hasModule(ModuleName.SELF_SERVICE)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="mesas"
                id="mesas-link"
                tablerIcon={<IconBuildingBridge2 color="#4298b5" stroke={2} />}
                icon=""
                label="Mis Mesas"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.SELF_SERVICE)}
              />
            )}

            {(isFreemium || hasModule(ModuleName.COBROS_EN_LINEA)) && !isFetching && commerce?.business_id && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="pedidos-en-linea"
                id="pedidos-en-linea-link"
                icon={onlinePaymentIcon}
                label="Cobros en línea"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.COBROS_EN_LINEA)}
              />
            )}

            {(isFreemium || hasModule(ModuleName.ANUNCIOS)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="anuncios"
                id="anuncios-link"
                icon={BtnPubli}
                label="Crear anuncios"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.ANUNCIOS)}
              />
            )}

            {(isFreemium || hasModule(ModuleName.COMPARTIR)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="compartir-menu"
                id="compartir-link"
                icon={BtnQR}
                label="Compartir menú"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.COMPARTIR)}
              />
            )}

            {(isFreemium || hasModule(ModuleName.LINK_PROFILE)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="perfil-social"
                id="perfil-social-link"
                tablerIcon={<IconLinkPlus color="#4298b5" stroke={2} />}
                icon=""
                label="Perfil Social"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.LINK_PROFILE)}
              />
            )}

            {(isFreemium || hasModule(ModuleName.ASISTENCIA_TECNICA)) && (
              <NavButtonV3
                isExpanded={isExpanded}
                to="asistencia"
                id="asistencia-link"
                icon={IconoQ}
                label="Asistencia Técnica"
                notIncludedOnCurrentPlan={!hasModule(ModuleName.ASISTENCIA_TECNICA)}
              />
            )}

            {/* Dropdown Ajustes */}
            <div className="flex flex-col">
              <button
                onClick={() => setIsConfigOpen(!isConfigOpen)}
                className={`flex items-center px-2 py-2 text-sm rounded-md hover:bg-color-gray_light transition-colors text-color-gray_ultra_dark ${
                  isConfigOpen ? 'bg-color-gray_light' : ''
                }`}
              >
                <Icon icon="fa6-solid:gear" color="#4298b5" width="20" className="w-5 flex-shrink-0" />
                <div
                  className={`overflow-hidden text-left transition-all ${isExpanded ? 'ml-2 w-full opacity-100' : 'w-0 opacity-0'}`}
                >
                  <span className="whitespace-nowrap">Ajustes</span>
                </div>
                {isExpanded && (
                  <Icon
                    icon="bxs:chevron-down"
                    className={`transition-transform flex-shrink-0 ${isConfigOpen ? 'rotate-180' : ''}`}
                    width="16"
                  />
                )}
              </button>

              {isConfigOpen && isExpanded && (
                <div className="flex flex-col pl-4 mt-1">
                  {(isFreemium || hasModule(ModuleName.DATOS_COMERCIO)) && (
                    <button
                      onClick={() =>
                        navigate(getNavigatePath(hasModule(ModuleName.DATOS_COMERCIO) || false, 'datos-del-comercio'))
                      }
                      className="flex items-center px-2 py-2 text-sm rounded-md hover:bg-color-gray_light text-color-gray_ultra_dark"
                    >
                      <Icon icon="fa-solid:store" color="#4298b5" width="16" className="mr-2" />
                      <span>Datos del comercio</span>
                      {!hasModule(ModuleName.DATOS_COMERCIO) && (
                        <Icon icon="solar:tag-price-bold" color="#FF3939" width="14" className="ml-auto" />
                      )}
                    </button>
                  )}

                  {(isFreemium || hasModule(ModuleName.HORARIOS)) && (
                    <button
                      onClick={() => navigate(getNavigatePath(hasModule(ModuleName.HORARIOS) || false, 'horarios'))}
                      className="flex items-center px-2 py-2 text-sm rounded-md hover:bg-color-gray_light text-color-gray_ultra_dark"
                    >
                      <Icon icon="bxs:time-five" color="#4298b5" width="16" className="mr-2" />
                      <span>Horarios</span>
                      {!hasModule(ModuleName.HORARIOS) && (
                        <Icon icon="solar:tag-price-bold" color="#FF3939" width="14" className="ml-auto" />
                      )}
                    </button>
                  )}

                  {(isFreemium || hasModule(ModuleName.TARIFAS)) && (
                    <button
                      onClick={() => navigate(getNavigatePath(hasModule(ModuleName.TARIFAS) || false, 'tarifas'))}
                      className="flex items-center px-2 py-2 text-sm rounded-md hover:bg-color-gray_light text-color-gray_ultra_dark"
                    >
                      <img src={priceIcon} alt="Tarifas" className="w-4 h-4 mr-2" />
                      <span>Tarifas y entregas</span>
                      {!hasModule(ModuleName.TARIFAS) && (
                        <Icon icon="solar:tag-price-bold" color="#FF3939" width="14" className="ml-auto" />
                      )}
                    </button>
                  )}

                  {(isFreemium || hasModule(ModuleName.USUARIOS)) && (
                    <button
                      onClick={() => navigate(getNavigatePath(hasModule(ModuleName.USUARIOS) || false, 'usuarios'))}
                      className="flex items-center px-2 py-2 text-sm rounded-md hover:bg-color-gray_light text-color-gray_ultra_dark"
                    >
                      <img src={UsersIcon} alt="Usuarios" className="w-4 h-4 mr-2" />
                      <span>Alta de usuarios</span>
                      {!hasModule(ModuleName.USUARIOS) && (
                        <Icon icon="solar:tag-price-bold" color="#FF3939" width="14" className="ml-auto" />
                      )}
                    </button>
                  )}

                  {(isFreemium || hasModule(ModuleName.AJUSTES)) && (
                    <button
                      onClick={() => navigate(getNavigatePath(hasModule(ModuleName.AJUSTES) || false, 'ajustes'))}
                      className="flex items-center px-2 py-2 text-sm rounded-md hover:bg-color-gray_light text-color-gray_ultra_dark"
                    >
                      <Icon icon="fa6-solid:gear" color="#4298b5" width="16" className="mr-2" />
                      <span>Ajustes</span>
                      {!hasModule(ModuleName.AJUSTES) && (
                        <Icon icon="solar:tag-price-bold" color="#FF3939" width="14" className="ml-auto" />
                      )}
                    </button>
                  )}

                  <button
                    onClick={() => navigate('/integraciones')}
                    className="flex items-center px-2 py-2 text-sm rounded-md hover:bg-color-gray_light text-color-gray_ultra_dark"
                  >
                    <Icon icon="mdi:plug" color="#4298b5" width="16" className="mr-2" />
                    <span>Integraciones</span>
                  </button>

                  <button
                    onClick={() => {
                      trackEvent(TRACK_EVENT_NAMES.FeedbackButtonClicked);
                      setShowFounderWarning(true);
                    }}
                    className="flex items-center px-2 py-2 text-sm rounded-md hover:bg-color-gray_light text-color-gray_ultra_dark"
                  >
                    <Icon icon="tabler:message-2-filled" color="#4298b5" width="16" className="mr-2" />
                    <span>Ayúdanos a mejorar</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sección inferior: Ir a mi menú, Mi cuenta, Cerrar sesión */}
          <div className="flex-shrink-0 border-t pt-3 mt-2 flex flex-col gap-0.5" style={{ borderColor: '#e5e7eb' }}>
            {/* Ir a mi menú */}
            <Tooltip label="Ir a mi menú" enabled={!isExpanded}>
              <button
                onClick={handleGoToMenu}
                className="flex items-center px-2 py-2 rounded-md hover:bg-color-gray_light transition-colors w-full text-color-gray_ultra_dark"
              >
                <Icon icon="tabler:external-link" color="#4298b5" width="20" className="w-5 flex-shrink-0" />
                <div
                  className={`overflow-hidden text-left transition-all ${isExpanded ? 'ml-2 w-full opacity-100' : 'w-0 opacity-0'}`}
                >
                  <p className="text-sm whitespace-nowrap font-medium">Ir a mi menú</p>
                  <p className="text-xs whitespace-nowrap" style={{ color: '#9ca3af' }}>
                    Ver el menú público
                  </p>
                </div>
              </button>
            </Tooltip>

            {/* Mi cuenta */}
            <Tooltip label="Mi cuenta" enabled={!isExpanded}>
              <button
                onClick={() => navigate('mi-cuenta')}
                className="flex items-center px-2 py-2 rounded-md hover:bg-color-gray_light transition-colors w-full text-color-gray_ultra_dark"
              >
                <Icon icon="fa-solid:user" color="#4298b5" width="20" className="w-5 flex-shrink-0" />
                <div
                  className={`overflow-hidden text-left transition-all ${isExpanded ? 'ml-2 w-full opacity-100' : 'w-0 opacity-0'}`}
                >
                  <p className="text-sm whitespace-nowrap font-medium">Mi cuenta</p>
                  <p className="text-xs whitespace-nowrap" style={{ color: '#9ca3af' }}>
                    Perfil y suscripción
                  </p>
                </div>
              </button>
            </Tooltip>

            {/* Cerrar sesión */}
            <Tooltip label="Cerrar sesión" enabled={!isExpanded}>
              <button
                onClick={logout}
                className="flex items-center px-2 py-2 rounded-md hover:bg-red-50 transition-colors w-full"
              >
                <Icon icon="tabler:logout" color="#FF3939" width="20" className="w-5 flex-shrink-0" />
                <div
                  className={`overflow-hidden text-left transition-all ${isExpanded ? 'ml-2 w-full opacity-100' : 'w-0 opacity-0'}`}
                >
                  <span className="text-sm whitespace-nowrap font-medium" style={{ color: '#FF3939' }}>
                    Cerrar sesión
                  </span>
                </div>
              </button>
            </Tooltip>
          </div>
        </NavBarContainerV2>
      )}

      {showFounderWarning && (
        <FounderContactModal
          onClose={() => setShowFounderWarning(false)}
          onContinue={() => {
            setShowFounderWarning(false);
            setShowFounderForm(true);
          }}
        />
      )}
      {showFounderForm && <FounderMessageForm commerce={commerce} onClose={() => setShowFounderForm(false)} />}
    </NavBarBackground>
  );
};

export default NavBarDesktopV3;
